import type { InvopopRegistrationMetadata, InvopopRegistrationResponse, InvopopSupplierStatus } from '@/api/invopop-adapter/types.ts'

export type RegistrationUiStatus =
  | 'not_registered'
  | 'processing'
  | 'pending_approval'
  | 'active'
  | 'pending_unregister'
  | 'error'

export type RegistrationRole = 'primary' | 'subsequent'

export type RegistrationState = {
  status: RegistrationUiStatus
  raw_status: InvopopSupplierStatus | null
  registration_iframe_url: string | null
  registration_role: RegistrationRole | null
  last_error: string | null
}

export type RegistrationFailureLogDetails = {
  adapter_status: InvopopSupplierStatus | null
  silo_entry_id: string | null
  job_id: string | null
  has_rejection_reason: boolean
}

export type RegistrationFailureLogEntry = {
  source: string
  reason: string
  details: RegistrationFailureLogDetails
  recorded_at: string
}

export const REGISTRATION_STATUS_KEYS: Record<RegistrationUiStatus, string> = {
  not_registered: 'payments.verifactu.registration.status.not_registered',
  processing: 'payments.verifactu.registration.status.processing',
  pending_approval: 'payments.verifactu.registration.status.pending_approval',
  active: 'payments.verifactu.registration.status.active',
  pending_unregister: 'payments.verifactu.registration.status.pending_unregister',
  error: 'payments.verifactu.registration.status.error',
}

export const REGISTRATION_UI_STATUS = {
  NOT_REGISTERED: 'not_registered',
  PROCESSING: 'processing',
  PENDING_APPROVAL: 'pending_approval',
  ACTIVE: 'active',
  PENDING_UNREGISTER: 'pending_unregister',
  ERROR: 'error',
} as const satisfies Record<string, RegistrationUiStatus>

export const REGISTRATION_POLL_INTERVAL_MS = 3000
export const REGISTRATION_POLL_MAX_ATTEMPTS = 200

const PENDING_UNREGISTER_ADAPTER_STATUSES = new Set<InvopopSupplierStatus>(['REJECTED'])
const RETRYABLE_FAILURE_ADAPTER_STATUSES = new Set<InvopopSupplierStatus>(['ERROR'])
const IN_PROGRESS_STATUSES = new Set<InvopopSupplierStatus>(['STARTED', 'PROCESSING', 'WAITING_FOR_UPLOAD'])
const COMPLETED_ADAPTER_STATUSES = new Set<InvopopSupplierStatus>(['REGISTERED', 'APPROVED'])

export const FALLBACK_REGISTRATION_FAILURE_REASON = 'an unexpected system validation error'

const ADAPTER_STATUS_MAP: Record<InvopopSupplierStatus, RegistrationUiStatus> = {
  STARTED: 'processing',
  PROCESSING: 'processing',
  WAITING_FOR_UPLOAD: 'processing',
  REGISTERED: 'pending_approval',
  APPROVED: 'active',
  REJECTED: 'pending_unregister',
  UNREGISTERED: 'not_registered',
  ERROR: 'error',
}

export function hasPriorRegistrationAttempt (
  registration: Pick<RegistrationState, 'status'>
): boolean {
  return registration.status !== REGISTRATION_UI_STATUS.NOT_REGISTERED
}

export function createEmptyRegistrationState (): RegistrationState {
  return {
    status: REGISTRATION_UI_STATUS.NOT_REGISTERED,
    raw_status: null,
    registration_iframe_url: null,
    registration_role: null,
    last_error: null,
  }
}

function normalizeAdapterStatus (status: string): InvopopSupplierStatus | null {
  const normalized = status.toUpperCase()

  return normalized in ADAPTER_STATUS_MAP
    ? normalized as InvopopSupplierStatus
    : null
}

export function resolveAdapterStatusFromPayload (payload: unknown): InvopopSupplierStatus | null {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const data = payload as Record<string, unknown>
  if (typeof data.status === 'string') {
    return normalizeAdapterStatus(data.status)
  }

  const detail = data.detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const detailStatus = (detail as Record<string, unknown>).status
    if (typeof detailStatus === 'string') {
      return normalizeAdapterStatus(detailStatus)
    }
  }

  return null
}

export function normalizeRegistrationResponse (
  data: Partial<InvopopRegistrationResponse> & { last_error?: string | null; detail?: string | Record<string, unknown> | null } = {},
  options: { role?: RegistrationRole | null } = {}
): RegistrationState {
  const rawStatus = resolveAdapterStatusFromPayload(data)

  let status: RegistrationUiStatus = 'not_registered'
  if (rawStatus) {
    status = ADAPTER_STATUS_MAP[rawStatus] ?? 'not_registered'
  }

  const hasRejection = hasAdapterRejectionReason(data)
  if (hasRejection && rawStatus && IN_PROGRESS_STATUSES.has(rawStatus)) {
    status = 'error'
  }

  const registrationUrl = data.registration_url || null
  const registrationIframeUrl = rawStatus && (
    COMPLETED_ADAPTER_STATUSES.has(rawStatus)
    || rawStatus === 'UNREGISTERED'
    || PENDING_UNREGISTER_ADAPTER_STATUSES.has(rawStatus)
    || (hasRejection && IN_PROGRESS_STATUSES.has(rawStatus))
  )
    ? null
    : registrationUrl

  return {
    status,
    raw_status: rawStatus,
    registration_iframe_url: registrationIframeUrl,
    registration_role: options.role ?? null,
    last_error: data.last_error || (typeof data.detail === 'string' ? data.detail : null) || null,
  }
}

export function isPendingUnregisterAdapterStatus (status: string | null | undefined): boolean {
  return typeof status === 'string' && PENDING_UNREGISTER_ADAPTER_STATUSES.has(status.toUpperCase() as InvopopSupplierStatus)
}

export function isRetryableFailureAdapterStatus (status: string | null | undefined): boolean {
  return typeof status === 'string' && RETRYABLE_FAILURE_ADAPTER_STATUSES.has(status.toUpperCase() as InvopopSupplierStatus)
}

export function isUnregisteredAdapterStatus (status: string | null | undefined): boolean {
  return typeof status === 'string' && status.toUpperCase() === 'UNREGISTERED'
}

export function hasAdapterRejectionReason (payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') {
    return false
  }

  const reason = (payload as Record<string, unknown>).rejection_reason
  return typeof reason === 'string' && reason.trim().length > 0
}

export function isInProgressAdapterStatus (status: string | null | undefined): boolean {
  return typeof status === 'string' && IN_PROGRESS_STATUSES.has(status.toUpperCase() as InvopopSupplierStatus)
}

export function isCompletedAdapterStatus (status: string | null | undefined): boolean {
  return typeof status === 'string' && COMPLETED_ADAPTER_STATUSES.has(status.toUpperCase() as InvopopSupplierStatus)
}

export type RegistrationFailureKind = 'pending_unregister' | 'retryable'

export const REGISTRATION_FAILURE_KIND = {
  PENDING_UNREGISTER: 'pending_unregister',
  RETRYABLE: 'retryable',
} as const satisfies Record<string, RegistrationFailureKind>

export function resolveRegistrationFailureKind (payload: unknown): RegistrationFailureKind | null {
  const status = resolveAdapterStatusFromPayload(payload)

  if (isCompletedAdapterStatus(status)) {
    return null
  }

  if (isPendingUnregisterAdapterStatus(status)) {
    return REGISTRATION_FAILURE_KIND.PENDING_UNREGISTER
  }

  if (isRetryableFailureAdapterStatus(status)) {
    return REGISTRATION_FAILURE_KIND.RETRYABLE
  }

  if (!hasAdapterRejectionReason(payload)) {
    return null
  }

  if (isUnregisteredAdapterStatus(status)) {
    return REGISTRATION_FAILURE_KIND.RETRYABLE
  }

  if (isInProgressAdapterStatus(status)) {
    return REGISTRATION_FAILURE_KIND.RETRYABLE
  }

  return REGISTRATION_FAILURE_KIND.RETRYABLE
}

function readMetadataObject (payload: unknown): InvopopRegistrationMetadata | null {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const data = payload as Record<string, unknown>
  if (data.metadata && typeof data.metadata === 'object' && !Array.isArray(data.metadata)) {
    return data.metadata as InvopopRegistrationMetadata
  }

  const detail = data.detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const detailRecord = detail as Record<string, unknown>
    if (detailRecord.metadata && typeof detailRecord.metadata === 'object') {
      return detailRecord.metadata as InvopopRegistrationMetadata
    }
  }

  return null
}

function readReasonFromMetadata (metadata: InvopopRegistrationMetadata | null): string | null {
  if (!metadata) {
    return null
  }

  const candidates = [metadata.reason, metadata.rejection_reason, metadata.message, metadata.description]
  const reason = candidates.find((value) => typeof value === 'string' && value.trim())
  return reason ? reason.trim() : null
}

function readReasonFromInvopopErrors (detail: Record<string, unknown>): string | null {
  const errors = detail.errors
  if (!Array.isArray(errors) || !errors.length) {
    return null
  }

  const message = errors
    .map((entry) => (entry as { message?: string })?.message)
    .filter(Boolean)
    .join('; ')

  return message || null
}

export function parseRegistrationMetadataReason (payload: unknown): string | null {
  const metadata = readMetadataObject(payload)
  const metadataReason = readReasonFromMetadata(metadata)
  if (metadataReason) {
    return metadataReason
  }

  if (!payload || typeof payload !== 'object') {
    return null
  }

  const data = payload as Record<string, unknown>

  if (typeof data.rejection_reason === 'string' && data.rejection_reason.trim()) {
    return data.rejection_reason.trim()
  }

  const detail = (data.detail ?? data) as Record<string, unknown>

  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const detailMetadataReason = readReasonFromMetadata(detail.metadata as InvopopRegistrationMetadata | null)
    if (detailMetadataReason) {
      return detailMetadataReason
    }

    const invopopReason = readReasonFromInvopopErrors(detail)
    if (invopopReason) {
      return invopopReason
    }

    if (typeof detail.message === 'string' && detail.message.trim()) {
      return detail.message.trim()
    }
  }

  if (typeof data.last_error === 'string' && data.last_error.trim()) {
    return data.last_error.trim()
  }

  return null
}

export function extractRegistrationFailure (payload: unknown) {
  const metadata = readMetadataObject(payload)
  const reason = parseRegistrationMetadataReason(payload) || FALLBACK_REGISTRATION_FAILURE_REASON

  return { metadata, reason, raw: payload }
}

export function extractRegistrationFailureDetails (payload: unknown): RegistrationFailureLogDetails {
  if (!payload || typeof payload !== 'object') {
    return {
      adapter_status: null,
      silo_entry_id: null,
      job_id: null,
      has_rejection_reason: false,
    }
  }

  const data = payload as Record<string, unknown>

  return {
    adapter_status: typeof data.status === 'string'
      ? normalizeAdapterStatus(data.status)
      : resolveAdapterStatusFromPayload(payload),
    silo_entry_id: typeof data.silo_entry_id === 'string' ? data.silo_entry_id : null,
    job_id: typeof data.job_id === 'string' ? data.job_id : null,
    has_rejection_reason: hasAdapterRejectionReason(payload),
  }
}

export function createRegistrationFailureLogEntry (
  failure: ReturnType<typeof extractRegistrationFailure>,
  source: string
): RegistrationFailureLogEntry {
  return {
    source,
    reason: failure.reason,
    details: extractRegistrationFailureDetails(failure.raw),
    recorded_at: new Date().toISOString(),
  }
}

export function toRegistrationFailureLoggerPayload (entry: RegistrationFailureLogEntry) {
  return {
    source: entry.source,
    adapter_status: entry.details.adapter_status,
    silo_entry_id: entry.details.silo_entry_id,
    job_id: entry.details.job_id,
    has_rejection_reason: entry.details.has_rejection_reason,
    recorded_at: entry.recorded_at,
  }
}

export function getSupplierRegistrationBlockedDetail (payload: unknown) {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const data = payload as Record<string, unknown>
  const detail = (data.detail ?? data) as Record<string, unknown>
  if (detail?.code !== 'SUPPLIER_REGISTRATION_FAILED') {
    return null
  }

  return detail
}

export function isSupplierRegistrationFailedPayload (payload: unknown): boolean {
  return getSupplierRegistrationBlockedDetail(payload) !== null
}

export function isSubsequentJoinBlockedError (
  error: { response?: { data?: unknown } },
  wasRegisteredBefore: boolean
): boolean {
  if (wasRegisteredBefore) {
    return false
  }

  return isSupplierRegistrationFailedPayload(error?.response?.data)
}

export function isPrimaryRegistrationBlockedError (
  error: { response?: { data?: unknown } },
  wasRegisteredBefore: boolean
): boolean {
  if (!wasRegisteredBefore) {
    return false
  }

  return isSupplierRegistrationFailedPayload(error?.response?.data)
}

export function isRegistrationInProgressError (error: { response?: { status?: number; data?: unknown } }): boolean {
  const status = error?.response?.status
  if (status !== 423 && status !== 409) {
    return false
  }

  const data = error?.response?.data as Record<string, unknown> | undefined
  if (!data) {
    return false
  }

  const detail = data.detail as Record<string, unknown> | undefined
  const code = data.error ?? detail?.error ?? detail?.code
  return code === 'registration_in_progress'
}

export function detectRegistrationRole ({
  putResponse,
  getResponse,
  wasRegisteredBefore,
}: {
  putResponse: Partial<InvopopRegistrationResponse> | null
  getResponse: Partial<InvopopRegistrationResponse> | null
  wasRegisteredBefore: boolean
}): RegistrationRole {
  if (putResponse?.is_primary === false || getResponse?.is_primary === false) {
    return 'subsequent'
  }

  if (putResponse?.is_primary === true || getResponse?.is_primary === true) {
    return 'primary'
  }

  if (putResponse?.registration_url) {
    return 'primary'
  }

  if (!putResponse?.registration_url && getResponse?.registration_url) {
    return 'subsequent'
  }

  const putStatus = putResponse?.status?.toUpperCase() as InvopopSupplierStatus | undefined
  if (putStatus && COMPLETED_ADAPTER_STATUSES.has(putStatus) && !wasRegisteredBefore) {
    return 'subsequent'
  }

  return 'primary'
}

export function isSubsequentJoinedInProgress ({
  putResponse,
  getResponse,
  wasRegisteredBefore,
}: {
  putResponse: Partial<InvopopRegistrationResponse> | null
  getResponse: Partial<InvopopRegistrationResponse> | null
  wasRegisteredBefore: boolean
}): boolean {
  if (wasRegisteredBefore) {
    return false
  }

  const role = detectRegistrationRole({ putResponse, getResponse, wasRegisteredBefore })
  if (role !== 'subsequent') {
    return false
  }

  const putStatus = putResponse?.status?.toUpperCase() as InvopopSupplierStatus | undefined
  return Boolean(putStatus && IN_PROGRESS_STATUSES.has(putStatus))
}

export function isTaxIdValidationError (error: { response?: { data?: { detail?: unknown } } }): boolean {
  const detail = error?.response?.data?.detail
  if (!Array.isArray(detail)) {
    return false
  }

  return detail.some((entry) => (entry as { loc?: string[] })?.loc?.includes?.('tax_id'))
}

export function parseInvopopRegistrationError (error: { response?: { data?: { detail?: unknown } } }): string | null {
  const detail = error?.response?.data?.detail

  if (typeof detail === 'string' && detail.trim()) {
    return detail
  }

  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const record = detail as Record<string, unknown>
    if (typeof record.message === 'string' && record.message.trim()) {
      return record.message.trim()
    }
  }

  if (Array.isArray(detail)) {
    const taxIdError = detail.find((entry) => (entry as { loc?: string[] })?.loc?.includes?.('tax_id'))
    const message = (taxIdError as { msg?: string })?.msg
      || detail.map((entry) => (entry as { msg?: string })?.msg).filter(Boolean).join(' ')
    if (message) {
      return message
    }
  }

  return null
}
